import {
  addProductsRequest,
  quickAddProductsBatchRequest,
  createOrGetCart,
  deleteCartProductRequest,
  duplicateCartProductsRequest,
  getCartById,
  getCartProducts,
  getAllAccountCarts,
  toggleSelectedCartProducts,
  requestCurrentCartProductsDetails,
  updateAccountCart,
  rejectCartRequest,
  refreshCart,
  submitCartRequest,
  updateCart,
  updateCartProduct
} from './actions';
import {
  assignCartProductGroups,
  clearCurrentCart,
  clearPendingSave,
  setCurrentCart,
  setCartAttribute,
  sortAndFilterCart,
  setCurrentCartTotals,
  setSizeQuantity,
  setPaymentMethod,
  setCustomerRequestDate,
  setUnitAllotmentId,
  setProductGroupAddress,
  setProductAddress,
  setCurrentCartHeader,
  updateCartProductPurchasables,
  updateCartEtag,
  validateCart as validateCartAction
} from './actionCreators';
import { selectShopDepartment } from '../shop/actions';
import { DEFAULT_CART_STRUCTURE, DEFAULT_CART_PRODUCT_STRUCTURE } from './util';
import { mapApiToCartStructure } from './util/apiMap';
import { buildCartProductGroups } from './util/productGroups';
import { PAYMENT_UNIT, paymentMethodsMap } from '../shop/constants';
import { validateCart } from './util/validation';
import { ADDON_CART_VALIDATION } from '../util/actionAddons';
import { findProductInfo } from '../productsInfo';
import {
  sortAndFilterCartProducts,
  computeProductTotals,
  computeCartTotals,
  setProductQuantity,
  setCartHeader
} from './util/helpers';
import { getMapKeyByValue } from '../util/getMapKeyByValue';

export const DEFAULT_CARTS_STATE = {
  accounts: {},
  currentCart: DEFAULT_CART_STRUCTURE
};

export const normalizeCart = cart => mapApiToCartStructure(cart);

export const normalizeProduct = product => ({
  ...DEFAULT_CART_PRODUCT_STRUCTURE,
  isDeleting: false,
  isDuplicating: false,
  isSelected: false,
  isPurchasable: false,
  ...product,
  paymentMethod: getMapKeyByValue(paymentMethodsMap, product.paymentMethod)
});

export const mergeOrAddCart = (carts, cart) => {
  const cartIds = carts.map(cart => cart.id);
  let cartsCopy = [...carts];

  if (cartIds.includes(cart.id)) {
    const index = cartIds.indexOf(cart.id);
    cartsCopy = Object.assign([], cartsCopy, { [index]: cart });
  } else {
    cartsCopy = [...cartsCopy, cart];
  }

  return cartsCopy;
};

const carts = (state = DEFAULT_CARTS_STATE, action) => {
  const { currentCart } = state;
  const productIds = (action.payload && action.payload.productIds) || [];

  switch (action.type) {
    case getAllAccountCarts.event.success: {
      const { accountNumber, carts } = action.payload;

      return {
        ...state,
        accounts: {
          ...state.accounts,
          [accountNumber]: carts.map(cart => normalizeCart(cart))
        }
      };
    }
    case refreshCart.event.success: {
      const { cart } = action.payload;
      const { headers } = normalizeCart(cart);
      const existingCarts = state.accounts[cart.accountNumber] || [];
      const updatedCart = {
        ...currentCart,
        headers
      };
      const validatedCart = validateCart(
        action,
        sortAndFilterCartProducts(action, computeCartTotals(action, updatedCart))
      );

      return {
        ...state,
        accounts: {
          ...state.accounts,
          [cart.accountNumber]: mergeOrAddCart(existingCarts, updatedCart)
        },
        currentCart: validatedCart
      };
    }
    case createOrGetCart.event.success:
    case getCartById.event.success:
    case updateAccountCart.event.success: {
      const { cart } = action.payload;
      const { accountNumber } = cart;
      const existingCarts = state.accounts[accountNumber] || [];
      const mappedCart = normalizeCart(cart);
      const shouldUpdate = [getCartById.event.success, createOrGetCart.event.success].includes(action.type);
      const currentCart = !shouldUpdate
        ? state.currentCart
        : validateCart(action, sortAndFilterCartProducts(action, computeCartTotals(action, mappedCart)));

      return {
        ...state,
        accounts: {
          ...state.accounts,
          [accountNumber]: mergeOrAddCart(existingCarts, mappedCart)
        },
        currentCart
      };
    }
    case setCurrentCart.type:
      return { ...state, currentCart: action.payload.cart };
    case clearCurrentCart.type:
    case selectShopDepartment.type:
      return { ...state, currentCart: DEFAULT_CARTS_STATE.currentCart };
    case addProductsRequest.event.success: {
      const { product: newProduct } = action.payload;
      const updatedProduct = computeProductTotals(action, newProduct);
      const updatedProducts = [
        ...currentCart.products.map(product => {
          if (productIds.includes(product.id)) {
            return {
              ...product,
              isDuplicating: false
            };
          }

          return product;
        }),
        updatedProduct
      ];
      const updatedCart = {
        ...currentCart,
        products: updatedProducts
      };

      return {
        ...state,
        currentCart: validateCart(action, sortAndFilterCartProducts(action, computeCartTotals(action, updatedCart)))
      };
    }
    case quickAddProductsBatchRequest.event.success: {
      const { products: newProducts } = action.payload;
      const newProductsWithTotals = newProducts.map(newProduct => computeProductTotals(action, newProduct));
      const updatedProducts = [...currentCart.products, ...newProductsWithTotals];
      const updatedCart = {
        ...currentCart,
        products: updatedProducts
      };

      return {
        ...state,
        currentCart: validateCart(action, sortAndFilterCartProducts(action, computeCartTotals(action, updatedCart)))
      };
    }
    case updateCartProductPurchasables.type: {
      const { productsInfoList } = action.addon[ADDON_CART_VALIDATION];
      const { products: cartProducts } = currentCart;
      const adjustedPurchasables = cartProducts.map(prod => ({
        ...prod,
        isPurchasable: findProductInfo(productsInfoList, prod.styleColor || prod.productCode) !== null
      }));
      const validatedCart = validateCart(action, {
        ...currentCart,
        products: adjustedPurchasables
      });

      return {
        ...state,
        currentCart: validatedCart
      };
    }
    case getCartProducts.event.start: {
      return {
        ...state,
        currentCart: {
          ...state.currentCart,
          hasFetchedProducts: false
        }
      };
    }
    case getCartProducts.event.success: {
      const { products } = action.payload;
      const updatedProducts = products.map(product => normalizeProduct(product));
      const updatedCart = {
        ...currentCart,
        products: updatedProducts,
        hasFetchedProducts: true
      };

      return {
        ...state,
        currentCart: validateCart(action, updatedCart)
      };
    }
    case duplicateCartProductsRequest.event.success: {
      const { product: newProduct, dupProductId } = action.payload;
      const currentCartProducts = [
        ...currentCart.products.map(product => {
          if (product.id === dupProductId) {
            return {
              ...product,
              isDuplicating: false
            };
          }

          return product;
        }),
        newProduct
      ];
      const updatedCart = {
        ...currentCart,
        products: currentCartProducts
      };

      return {
        ...state,
        currentCart: validateCart(action, sortAndFilterCartProducts(action, computeCartTotals(action, updatedCart)))
      };
    }
    case deleteCartProductRequest.event.success: {
      const { cartProductId } = action.payload;
      const products = currentCart.products.filter(({ id }) => id !== cartProductId);
      const updatedCart = {
        ...currentCart,
        products
      };

      return {
        ...state,
        currentCart: validateCart(action, sortAndFilterCartProducts(action, computeCartTotals(action, updatedCart)))
      };
    }
    case requestCurrentCartProductsDetails.event.success: {
      const updatedCart = {
        ...state.currentCart,
        productsMaxAmountsOrderable: action.payload.reduce(
          (detailsByProductCode, style) => ({
            ...detailsByProductCode,
            [style.productCode]: style.maxAmountOrderable
          }),
          state.currentCart.productsMaxAmountsOrderable
        )
      };
      return {
        ...state,
        currentCart: validateCart(action, updatedCart)
      };
    }
    case setCartAttribute.type:
      return {
        ...state,
        currentCart: {
          ...currentCart,
          [action.payload.name]: action.payload.value
        }
      };
    case setPaymentMethod.type: {
      const {
        payload: { method: paymentMethod }
      } = action;
      const updatedCart = {
        ...currentCart,
        pendingSave: true,
        products: currentCart.products.map(product => {
          if (productIds.includes(product.id)) {
            return {
              ...product,
              paymentMethod,
              unitAllotmentId: paymentMethod !== PAYMENT_UNIT ? '' : product.unitAllotmentId
            };
          }

          return product;
        })
      };

      return {
        ...state,
        currentCart: validateCart(action, sortAndFilterCartProducts(action, computeCartTotals(action, updatedCart)))
      };
    }
    case setCustomerRequestDate.type: {
      const {
        payload: { customerRequestDate }
      } = action;
      const updatedCart = {
        ...currentCart,
        pendingSave: true,
        products: currentCart.products.map(product => {
          if (productIds.includes(product.id)) {
            return {
              ...product,
              customerRequestDate
            };
          }

          return product;
        })
      };

      return {
        ...state,
        currentCart: validateCart(action, sortAndFilterCartProducts(action, computeCartTotals(action, updatedCart)))
      };
    }
    case setSizeQuantity.type: {
      const {
        payload: { productId, sizeDescription, quantity: newQuantity, sizeDisplay }
      } = action;
      const product = currentCart.products.find(product => product.id === productId);
      const productIndex = currentCart.products.indexOf(product);
      const updatedProduct = computeProductTotals(
        action,
        setProductQuantity(product, sizeDescription, newQuantity, sizeDisplay)
      );
      const updatedCart = {
        ...currentCart,
        pendingSave: newQuantity !== '0',
        products: Object.assign([], currentCart.products, {
          [productIndex]: computeProductTotals(action, updatedProduct)
        })
      };

      return {
        ...state,
        currentCart: validateCart(action, computeCartTotals(action, updatedCart), [productId])
      };
    }
    case setUnitAllotmentId.type: {
      const {
        payload: { unitAllotmentId, productId }
      } = action;
      const product = currentCart.products.find(product => product.id === productId);
      const productIndex = currentCart.products.indexOf(product);
      const updatedCart = {
        ...currentCart,
        pendingSave: true,
        products: Object.assign([], currentCart.products, {
          [productIndex]: {
            ...currentCart.products[productIndex],
            unitAllotmentId
          }
        })
      };

      return {
        ...state,
        currentCart: validateCart(action, computeCartTotals(action, updatedCart), [productId])
      };
    }
    case sortAndFilterCart.type: {
      return {
        ...state,
        currentCart: sortAndFilterCartProducts(action, currentCart)
      };
    }
    case setCurrentCartTotals.type: {
      const updatedCart = {
        ...currentCart,
        products: currentCart.products.map(product => computeProductTotals(action, product))
      };

      return {
        ...state,
        currentCart: validateCart(action, computeCartTotals(action, updatedCart))
      };
    }
    case toggleSelectedCartProducts.type: {
      return {
        ...state,
        currentCart: {
          ...currentCart,
          products: currentCart.products.map(product => {
            if (productIds.includes(product.id)) {
              return {
                ...product,
                isSelected: !product.isSelected
              };
            }

            return product;
          })
        }
      };
    }
    case duplicateCartProductsRequest.event.start:
    case duplicateCartProductsRequest.event.error:
    case deleteCartProductRequest.event.start:
    case deleteCartProductRequest.event.error: {
      const isDeleting = action.type === deleteCartProductRequest.event.start;
      const isDuplicating = action.type === duplicateCartProductsRequest.event.start;
      const { cartProductId } = action.payload;

      return {
        ...state,
        currentCart: {
          ...currentCart,
          products: currentCart.products.map(product => {
            if (product.id === cartProductId) {
              return {
                ...product,
                isDeleting,
                isDuplicating
              };
            }

            return product;
          })
        }
      };
    }
    case setCurrentCartHeader.type:
      return {
        ...state,
        currentCart: validateCart(action, setCartHeader(currentCart, action.payload.name, action.payload.value))
      };
    case assignCartProductGroups.type:
      return { ...state, currentCart: buildCartProductGroups(action, currentCart) };
    case setProductGroupAddress.type: {
      const { address, type } = action.payload;
      const updatedCart = {
        ...currentCart,
        productGroups: currentCart.productGroups.map(group => (group.type === type ? { ...group, address } : group))
      };

      return {
        ...state,
        currentCart: validateCart(action, updatedCart)
      };
    }
    case setProductAddress.type: {
      const { productId, addressId, addressType } = action.payload;

      return {
        ...state,
        currentCart: {
          ...currentCart,
          products: currentCart.products.map(product =>
            product.id === productId ? { ...product, addressType, addressId } : product
          )
        }
      };
    }
    case rejectCartRequest.event.success: {
      const { accountNumber, cartId } = action.payload;

      return {
        ...state,
        accounts: state.accounts[accountNumber].filter(cart => cart.id !== cartId),
        currentCart: DEFAULT_CARTS_STATE.currentCart
      };
    }
    case updateCartEtag.type: {
      return {
        ...state,
        currentCart: {
          ...currentCart,
          etag: action.payload.ETAG
        }
      };
    }
    case submitCartRequest.event.success: {
      const { accountNumber, cartId, cartStatus } = action.payload;
      const existingCarts = state.accounts[accountNumber] || [];

      return {
        ...state,
        accounts: {
          ...state.accounts,
          [accountNumber]: existingCarts.map(cart => (cart.id === cartId ? { ...cart, status: cartStatus } : cart))
        },
        currentCart: {
          ...currentCart,
          status: cartStatus
        }
      };
    }
    case validateCartAction.type: {
      return {
        ...state,
        currentCart: validateCart(action, currentCart)
      };
    }
    case clearPendingSave.type:
    case updateCartProduct.event.start:
    case updateCart.event.start:
      return {
        ...state,
        currentCart: {
          ...state.currentCart,
          pendingSave: false
        }
      };
    default:
      return state;
  }
};

export default carts;
